import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { styles } from '../theme';
import { API_URL, fetchWithAuth } from '../api';
import { exportReportToPDF } from '../utils/reportPdf';
import { formatCurrency } from '../utils/formatUtils';
import { getPHTDate, formatPHDatetime } from '../utils/dateUtils';

const fmt = (v) => formatCurrency(v);
const fmtDt = (v) => v ? formatPHDatetime(v) : '-';
const defaultDates = () => {
  const to = getPHTDate();
  const from = getPHTDate();
  from.setDate(from.getDate() - 7);
  const s = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: s(from), to: s(to) };
};

const PosShiftReport = ({ token }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { from, to } = defaultDates();
    setDateFrom(from);
    setDateTo(to);
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()).then(setBranches).catch(err => console.error(err));
  }, [token]);

  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, branch_id: branchId });
      const res = await fetchWithAuth(`${API_URL}/pos/reports/shifts?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, branchId]);

  useEffect(() => { if (dateFrom && dateTo) generate(); }, [dateFrom, dateTo, generate]);

  const totalVariance = rows.reduce((sum, r) => sum + Number(r.cash_variance || 0), 0);

  const varianceChartData = useMemo(() => {
    return rows
      .filter(r => r.cash_variance !== null)
      .map(r => ({
        label: r.opened_at ? (() => {
          const d = getPHTDate(r.opened_at);
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${months[d.getMonth()]} ${d.getDate()}`;
        })() : `#${r.id}`,
        cash_variance: Number(r.cash_variance || 0)
      }))
      .reverse();
  }, [rows]);

  const exportPdf = () => {
    exportReportToPDF({
      title: 'POS Shift Report',
      subtitle: `${dateFrom} to ${dateTo}`,
      columns: [
        { header: 'Opened', key: 'opened_at' }, { header: 'Closed', key: 'closed_at' }, { header: 'Branch', key: 'branch_name' },
        { header: 'Opened By', key: 'opened_by_operator_name' }, { header: 'Status', key: 'status' },
        { header: 'Starting', key: 'starting_cash', align: 'right' },
        { header: 'Counted', key: 'ending_cash_counted', align: 'right' },
        { header: 'Expected', key: 'expected_cash', align: 'right' },
        { header: 'Variance', key: 'cash_variance', align: 'right' }
      ],
      rows: rows.map(r => ({
        ...r, opened_at: fmtDt(r.opened_at), closed_at: fmtDt(r.closed_at),
        starting_cash: fmt(r.starting_cash), ending_cash_counted: r.ending_cash_counted === null ? '-' : fmt(r.ending_cash_counted),
        expected_cash: r.expected_cash === null ? '-' : fmt(r.expected_cash), cash_variance: r.cash_variance === null ? '-' : fmt(r.cash_variance)
      })),
      filename: `pos_shift_report_${dateFrom}_to_${dateTo}.pdf`
    });
  };

  return (
    <div>
      <h1 style={styles.title}>Shift Report</h1>
      <div style={{ ...styles.card, marginTop: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={styles.formGroup}><label style={styles.label}>From</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}><label style={styles.label}>To</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={styles.input}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={generate} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download size={18} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {rows.length > 0 && <button onClick={exportPdf} style={{ ...styles.button, ...styles.buttonSecondary }}>Export PDF</button>}
        </div>
      </div>

      {varianceChartData.length > 0 && (
        <div style={{ ...styles.card, marginTop: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Cash Variance by Shift</h3>
          <div style={{ height: '280px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={varianceChartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fill: '#4b5563', fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `₱${v.toLocaleString()}`} tick={{ fill: '#4b5563', fontSize: 12 }} />
                <Tooltip formatter={(v) => `₱${fmt(v)}`} />
                <ReferenceLine y={0} stroke="#9ca3af" />
                <Bar dataKey="cash_variance" name="Variance">
                  {varianceChartData.map((d, i) => (
                    <Cell key={i} fill={d.cash_variance < 0 ? '#b91c1c' : (d.cash_variance > 0 ? '#d97706' : '#059669')} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={{ ...styles.card, marginTop: '16px', padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Opened</th><th style={styles.th}>Closed</th><th style={styles.th}>Branch</th>
                <th style={styles.th}>Opened By</th><th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Starting</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Counted</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Expected</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>{fmtDt(r.opened_at)}</td>
                  <td style={styles.td}>{fmtDt(r.closed_at)}</td>
                  <td style={styles.td}>{r.branch_name}</td>
                  <td style={styles.td}>{r.opened_by_operator_name}</td>
                  <td style={styles.td}>
                    <span style={{ color: r.status === 'open' ? '#059669' : '#374151' }}>{r.status}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>₱{fmt(r.starting_cash)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.ending_cash_counted === null ? '-' : `₱${fmt(r.ending_cash_counted)}`}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.expected_cash === null ? '-' : `₱${fmt(r.expected_cash)}`}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: r.cash_variance > 0 ? '#d97706' : (r.cash_variance < 0 ? '#b91c1c' : '#059669') }}>
                    {r.cash_variance === null ? '-' : `₱${fmt(r.cash_variance)}`}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={9} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>No shifts for this period.</td></tr>
              )}
              {rows.length > 0 && (
                <tr style={{ backgroundColor: '#ecfeff' }}>
                  <td style={{ ...styles.td, fontWeight: 700 }} colSpan={8}>TOTAL VARIANCE</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>₱{fmt(totalVariance)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PosShiftReport;
